/**
 * Global Store
 *
 * Application-wide state that doesn't belong to any specific module.
 * Module-specific state should be in the module's store folder.
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

import { notifyAuthChange } from '@/app/providers';

import type { User, ThemeMode } from '@/shared/types';

// ============================================================================
// Types
// ============================================================================

interface AppState {
    // UI State
    sidebarCollapsed: boolean;
    theme: ThemeMode;

    // User State
    currentUser: User | null;
    isAuthenticated: boolean;

    // Global Loading
    globalLoading: boolean;
}

interface AppActions {
    // UI Actions
    toggleSidebar: () => void;
    setSidebarCollapsed: (collapsed: boolean) => void;
    setTheme: (theme: ThemeMode) => void;

    // Auth Actions
    login: (user: User, token: string) => void;
    setUser: (user: User | null) => void;
    logout: () => void;

    // Loading
    setGlobalLoading: (loading: boolean) => void;
}

type AppStore = AppState & AppActions;

// ============================================================================
// Initial State
// ============================================================================

const initialState: AppState = {
    sidebarCollapsed: false,
    theme: 'dark',
    currentUser: null,
    isAuthenticated: false,
    globalLoading: false,
};

// ============================================================================
// Store
// ============================================================================

export const useAppStore = create<AppStore>()(
    devtools(
        persist(
            (set) => ({
                ...initialState,

                toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

                setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),

                setTheme: (theme) => set({ theme }),

                /**
                 * Login - Sets user, token, and triggers SocketIO connection
                 */
                login: (user, token) => {
                    // Store token in localStorage
                    localStorage.setItem('auth_token', token);

                    // Update state
                    set({
                        currentUser: user,
                        isAuthenticated: true,
                    });

                    // Notify providers (triggers SocketIO connection)
                    notifyAuthChange();
                },

                setUser: (user) =>
                    set({
                        currentUser: user,
                        isAuthenticated: !!user,
                    }),

                /**
                 * Logout - Clears user, token, and disconnects SocketIO
                 */
                logout: () => {
                    // Clear token from localStorage
                    localStorage.removeItem('auth_token');

                    // Update state
                    set({
                        currentUser: null,
                        isAuthenticated: false,
                    });

                    // Notify providers (triggers SocketIO disconnection)
                    notifyAuthChange();
                },

                setGlobalLoading: (loading) => set({ globalLoading: loading }),
            }),
            {
                name: 'autocollect-app-store',
                partialize: (state) => ({
                    sidebarCollapsed: state.sidebarCollapsed,
                    theme: state.theme,
                }),
            }
        ),
        { name: 'app-store' }
    )
);

// ============================================================================
// Selectors
// ============================================================================

export const useCurrentUser = () => useAppStore((state) => state.currentUser);
export const useIsAuthenticated = () => useAppStore((state) => state.isAuthenticated);
export const useSidebarCollapsed = () => useAppStore((state) => state.sidebarCollapsed);

// ============================================================================
// Auth Helpers
// ============================================================================

/**
 * Get auth actions for login/logout
 *
 * @example
 * ```tsx
 * const { login, logout } = useAuth();
 *
 * // On successful authentication
 * login(userData, accessToken);
 *
 * // On logout
 * logout();
 * ```
 */
export const useAuth = () =>
    useAppStore((state) => ({
        login: state.login,
        logout: state.logout,
        isAuthenticated: state.isAuthenticated,
        currentUser: state.currentUser,
    }));
